import React from "react";
import { CARD_BG, SHADOW } from "../config/theme";

interface MetricCardProps {
  label?: string;
  value: string;
  title?: string;
  subtitle?: string;
  trend?: "up" | "down" | "neutral";
  icon?: React.ReactNode;
}

/**
 * Display a single metric such as Sales, Units, Spend or ROAS.  The card
 * presents the value prominently with the label/title and optional subtitle.
 * Supports trend indicators and icons for better visual communication.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  title,
  subtitle,
  trend = "neutral",
  icon,
}) => {
  const displayTitle = title || label;

  const trendColors = {
    up: "text-green-600 dark:text-green-400",
    down: "text-red-600 dark:text-red-400",
    neutral: "text-gray-600 dark:text-gray-400",
  };

  const trendIcons = {
    up: "↑",
    down: "↓",
    neutral: "•",
  };

  return (
    <div
      className={`${CARD_BG} rounded-lg ${SHADOW.cardHover} border border-gray-200 dark:border-[#5B5353] p-6`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-600 dark:text-[#B5A9AA] uppercase tracking-wide">
            {displayTitle}
          </div>
        </div>
        {icon && (
          <div className="text-gray-400 dark:text-[#948A8B]">{icon}</div>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-bold text-gray-900 dark:text-[#FFF8F9]">
          {value}
        </div>
        {trend !== "neutral" && (
          <span className={`text-lg ${trendColors[trend]}`}>
            {trendIcons[trend]}
          </span>
        )}
      </div>

      {subtitle && (
        <div className="text-sm text-gray-500 dark:text-[#B5A9AA] mt-2">
          {subtitle}
        </div>
      )}
    </div>
  );
};
